/**
 * Runs a room's townsfolk. Each NPC's day is a fixed loop of legs (walk, then
 * linger), so where it is follows from the shared server clock alone. The
 * legs are planned once with the same A* players use.
 */
import { NPCS, type Npc } from '../../../shared/npcs.ts'
import { findPath, SPEED, type Path } from '../../../shared/path.ts'
import { grid, type RoomId } from '../../../shared/world.ts'
import type { Player } from './state.ts'

interface Leg { pts: number[]; walk: number; wait: number; stop: Npc['stops'][number] }
interface Plan { npc: Npc; legs: Leg[]; total: number; player: Player; lastLeg: number }

export class Townsfolk {
  private plans: Plan[] = []
  room?: RoomId

  setRoom(room: RoomId): void {
    if (this.room === room) return
    this.room = room
    const g = grid(room)
    this.plans = NPCS[room].map((npc, n) => {
      const legs: Leg[] = []
      let total = 0
      npc.stops.forEach((stop, i) => {
        const from = npc.stops[(i + npc.stops.length - 1) % npc.stops.length]!
        const tiles = findPath(g, from.x, from.y, stop.x, stop.y) ?? [[stop.x, stop.y]]
        const pts = tiles.flatMap(([x, y]) => [x + 0.5, y + 0.5])
        let len = 0
        for (let k = 2; k < pts.length; k += 2) len += Math.hypot(pts[k]! - pts[k - 2]!, pts[k + 1]! - pts[k - 1]!)
        const walk = (len / npc.speed) * 1000
        legs.push({ pts, walk, wait: stop.wait * 1000, stop })
        total += walk + stop.wait * 1000
      })
      const player: Player = { id: npc.id, name: npc.name, look: npc.look, path: { t0: 0, pts: [npc.stops[0]!.x + 0.5, npc.stops[0]!.y + 0.5] }, pose: 'stand', face: 1, phase: n * 1.7 }
      return { npc, legs, total, player, lastLeg: -1 }
    })
  }

  /**
   * Advance everyone to `now` (local clock); `offset` makes it server time so
   * all clients agree. Calls `arrive` once whenever an NPC reaches a stop.
   */
  update(now: number, offset: number, arrive: (p: Player, npc: Npc, stop: Npc['stops'][number]) => void): Player[] {
    const out: Player[] = []
    const server = now + offset
    for (const plan of this.plans) {
      const { legs, total, player } = plan
      // Each NPC starts its loop at a different point so they don't move in lockstep.
      let t = (server + plan.npc.id.length * 7919) % total
      let i = 0
      for (; i < legs.length; i++) {
        const span = legs[i]!.walk + legs[i]!.wait
        if (t < span) break
        t -= span
      }
      const leg = legs[Math.min(i, legs.length - 1)]!
      // posAt walks at the player SPEED; shift t0 so the distance covered matches the NPC's own pace.
      const path: Path = { t0: now - (t * plan.npc.speed) / SPEED, pts: leg.pts }
      player.path = path
      const arrived = t >= leg.walk
      player.pose = arrived && leg.stop.nap ? 'nap' : 'stand'
      if (arrived && plan.lastLeg !== i) {
        plan.lastLeg = i
        arrive(player, plan.npc, leg.stop)
      } else if (!arrived && plan.lastLeg === i) {
        plan.lastLeg = -1
      }
      out.push(player)
    }
    return out
  }

  npc(id: string): Npc | undefined {
    return this.plans.find((p) => p.npc.id === id)?.npc
  }
}
